import { DateTime } from "luxon";
import { ReactNode, createContext, useContext, useEffect, useState } from "react";

type ClockContextType = {
    currentTime: DateTime;
    startClock: () => void;
    stopClock: () => void;
};

const ClockContext = createContext<ClockContextType | null>(null);

type ClockProviderProps = {
    children: ReactNode;
};

/**
 * Clock Provider that manages time updates
 * Provides current time to all child components
 */
export const ClockProvider = ({ children }: ClockProviderProps) => {
    const [currentTime, setCurrentTime] = useState<DateTime>(DateTime.now());
    const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

    const startClock = () => {
        if (intervalId) return; // Already running

        const newIntervalId = setInterval(() => {
            setCurrentTime(DateTime.now());
        }, 1000); // Update every second

        setIntervalId(newIntervalId);
    };

    const stopClock = () => {
        if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
        }
    };

    // Auto-start clock when provider mounts
    useEffect(() => {
        startClock();

        return () => {
            stopClock();
        };
    }, []);

    return <ClockContext.Provider value={{ currentTime, startClock, stopClock }}>{children}</ClockContext.Provider>;
};

/**
 * Hook to use clock context
 * Provides current time and clock control functions
 */
export const useClock = () => {
    const context = useContext(ClockContext);

    if (!context) {
        throw new Error("useClock must be used within a ClockProvider");
    }

    return context;
};
